/**
 * ToastManager - Centralized toast notification management
 * Handles validation, deduplication, and queue limits
 */

import { toast } from "vue-sonner";

class ToastManager {
  private activeToasts: Map<string, string | number> = new Map();
  private recentMessages: Set<string> = new Set();
  private maxToasts: number = 3;
  private deduplicationWindow: number = 3000; // 3 seconds

  /**
   * Validate message - prevent blank/empty toasts
   */
  validateMessage(message: any): string | null {
    // Handle various falsy values
    if (message === null || message === undefined) {
      return null;
    }

    // Convert to string
    const msgStr = typeof message === "string" ? message : String(message);

    // Trim and check if empty
    const trimmed = msgStr.trim();
    if (trimmed.length === 0) {
      return null;
    }

    return trimmed;
  }

  /**
   * Check if message was recently shown (deduplication)
   */
  isDuplicate(message: string): boolean {
    const messageKey = message.toLowerCase().trim();

    if (this.recentMessages.has(messageKey)) {
      return true;
    }

    // Add to recent messages
    this.recentMessages.add(messageKey);

    // Remove from recent messages after deduplication window
    setTimeout(() => {
      this.recentMessages.delete(messageKey);
    }, this.deduplicationWindow);

    return false;
  }

  /**
   * Enforce maximum toast limit - dismiss oldest if needed
   */
  enforceLimit(): void {
    if (this.activeToasts.size >= this.maxToasts) {
      // Get first (oldest) toast
      const firstKey = this.activeToasts.keys().next().value as string | undefined;
      if (firstKey) {
        const toastId = this.activeToasts.get(firstKey);
        if (toastId !== undefined) {
          toast.dismiss(toastId);
        }
        this.activeToasts.delete(firstKey);
      }
    }
  }

  /**
   * Track an active toast
   */
  trackToast(message: string, toastId: string | number): void {
    const key = `${Date.now()}-${message}`;
    this.activeToasts.set(key, toastId);

    // Auto-remove from tracking when dismissed
    setTimeout(() => {
      this.activeToasts.delete(key);
    }, 6000); // Max duration + buffer
  }

  /**
   * Get current number of active toasts
   */
  getActiveCount(): number {
    return this.activeToasts.size;
  }

  /**
   * Clear all active toasts
   */
  clearAll(): void {
    this.activeToasts.forEach((toastId) => {
      toast.dismiss(toastId);
    });
    this.activeToasts.clear();
  }
}

// Export singleton instance
export const toastManager = new ToastManager();
